import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { getIn } from 'formik';
import { Item } from 'react-stately';
import { useFilter } from 'react-aria';
import ComboBox from './UncontrolledComboBox';

const LIST_PAGE_SIZE = 30;

function FormikControlledComboBox(props) {
  const { options, field, form, labelMsg, isRequired } = props;

  const timerRef = useRef(null);

  const { name, value = null } = field;

  const [inputValue, setInputValue] = useState(value ? value.name : '');
  const [items, setItems] = useState(options);
  const [listCutOff, setListCutOff] = useState(LIST_PAGE_SIZE);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
    },
    []
  );

  const { contains } = useFilter({ sensitivity: 'base' });

  const handleBlur = () => {
    form.setFieldTouched(name, true);
  };

  const handleLoadMore = () => {
    setListCutOff((prevState) => {
      if (prevState + 10 > items.length) {
        return items.length;
      }
      return prevState + 10;
    });
  };

  const onSelectionChange = (key) => {
    const selectedItem = items.find((option) => option.id === key);

    setInputValue((selectedItem && selectedItem.name) ?? '');
    setItems(selectedItem ? [selectedItem] : options);
    setListCutOff(LIST_PAGE_SIZE);
    if (selectedItem) {
      form.setFieldValue(name, selectedItem);
    } else {
      form.setFieldValue(name, null);
    }
  };

  const updateSearchResults = (textInputValue) => {
    if (!textInputValue) {
      setItems(options);
    } else {
      const newOptions = options.filter((item) =>
        contains(item.name, textInputValue)
      );
      setItems(newOptions);
    }
    setIsLoading(false);
  };

  const delayedSearchResultUpdate = (textInputValue) => {
    clearTimeout(timerRef.current);
    setIsLoading(true);
    setListCutOff(LIST_PAGE_SIZE);
    timerRef.current = setTimeout(
      () => updateSearchResults(textInputValue),
      500
    );
  };

  const handleKeyPress = (textInputValue) => {
    setInputValue(textInputValue);
    setItems([]);
    delayedSearchResultUpdate(textInputValue);
  };

  const onOpenChange = (isOpen, menuTrigger) => {
    if (menuTrigger === 'manual' && isOpen) {
      setItems(options);
    }
  };

  let isError = false;
  const touched = getIn(form.touched, name);
  const errors = getIn(form.errors, name);
  if (touched && errors) {
    isError = true;
  }

  return (
    <ComboBox
      name={name}
      label={<FormattedMessage id={labelMsg} />}
      items={items.slice(0, listCutOff)}
      selectedKey={value ? value.id : null}
      inputValue={inputValue}
      onOpenChange={onOpenChange}
      onSelectionChange={onSelectionChange}
      onInputChange={handleKeyPress}
      handleLoadMore={handleLoadMore}
      isLoading={isLoading}
      onBlur={handleBlur}
      isError={isError}
      isRequired={isRequired}
    >
      {(item) => <Item>{item.name}</Item>}
    </ComboBox>
  );
}

FormikControlledComboBox.defaultProps = {
  isRequired: false,
};

FormikControlledComboBox.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
  }).isRequired,
  form: PropTypes.shape({
    setFieldValue: PropTypes.func.isRequired,
    setFieldTouched: PropTypes.func.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    touched: PropTypes.object,
    // eslint-disable-next-line react/forbid-prop-types
    errors: PropTypes.object,
  }).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  labelMsg: PropTypes.string.isRequired,
  isRequired: PropTypes.bool,
};

export default FormikControlledComboBox;
